import { emitHintSignal } from '@flobi/asset-library/hints';
import { defaultLayerAnimation, MAGIC_STYLES, magicOptions, type Direction, type Ease, type LayerAnimation, type MagicChoices, type MotionType, type Recipe, type RecipeSettings } from '@flobi/artwork-core';
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode, type WheelEvent } from 'react';
import type { AnimationTarget } from '../useEditorCanvas';
import { NumberField, PopUpButton, SegmentedControl, Slider } from '../../ui/controls';
import { Icon, ICON_SIZE } from '../chrome/icons';
import { Menu, useMenu } from '../../ui/menu';
import { Popover } from '../../ui/popover';
import { tip } from '../../ui/tooltip';
import { Arrow, MoveTile, RecipeTile, type LayerPicture } from './AnimateThumbs';
import { useProTools } from '../pro';

/** Slow–Fast, 0–100, over the span a move takes: 3 s at Slow, 0.2 s at Fast. */
const SLOWEST = 3;
const FASTEST = 0.2;
function paceOf(span: number): number {
  return Math.round(Math.min(100, Math.max(0, ((SLOWEST - span) / (SLOWEST - FASTEST)) * 100)));
}
function spanOf(pace: number): number {
  return Math.round((SLOWEST - (pace / 100) * (SLOWEST - FASTEST)) * 100) / 100;
}
import {
  arrowAngle,
  controlsFor,
  DIRECTION_NAMES,
  normalise,
  paceAt,
  paceValue,
  PAD,
  phaseValues,
  presetOf,
  presetName,
  presetsFor,
  recipeGroups,
  spreadAt,
  spreadValue,
  TEXT_BY_NAMES,
  textByOf,
  tryOut,
  withPhase,
  withPreset,
  type Phase,
  type PresetValue,
  type RecipeLike,
} from './animateModel';
import { TimingStrip } from './TimingStrip';
import type { MotionPhase, MotionResize, Span, StripInput } from './timingStripModel';

/**
 * A change to the picked layers' animation, worked out from each one's own (null: none
 * yet). A layer with none of its own also comes with `inherited` — what it plays from its
 * page — so its first change keeps what it was already doing.
 */
export type LayerEdit = (own: LayerAnimation | null, inherited: LayerAnimation | null) => LayerAnimation | null;

/** Nothing playing; a page playing; a page held on one instant (paused, or the playhead dragged). */
export type Playback = 'idle' | 'playing' | 'held';

const STUDIO_PHASES = [['in', 'Enter'], ['loop', 'Loop'], ['out', 'Exit']] as const;
const EASING_OPTIONS: readonly (readonly [Ease, string])[] = [
  ['smooth', 'Smooth'], ['out', 'Ease out'], ['inOut', 'Ease in out'], ['linear', 'Linear'],
  ['back', 'Overshoot'], ['snappy', 'Snappy'], ['spring', 'Spring'], ['bounce', 'Bounce'],
];

/** Full-width animation studio: scope, phases, presets or timing, and direct settings. */
export function AnimateBar({
  target,
  recipe,
  choices,
  suggestRecipe,
  picture,
  playback,
  timing,
  loop,
  hint,
  drawingPath,
  strip,
  selected,
  playhead,
  onPlay,
  onPause,
  onRecipe,
  onPlayPage,
  onRemove,
  onEdit,
  onTry,
  onWholePage,
  onClose,
  onTiming,
  onLoop,
  onDrawPath,
  onSelect,
  onWindow,
  onDelay,
  onResizeMotion,
  onLength,
  onSeek,
  onHeight,
}: {
  target: AnimationTarget | null;
  /** The recipe the page wears exactly, with how it was adjusted, or null. */
  recipe: Recipe | null;
  /** Which of that recipe's adjustments do something on this page: what its gear shows. */
  choices: MagicChoices | null;
  /** What the picked layer looks like, for its moves' tiles. */
  picture: LayerPicture;
  playback: Playback;
  /** Whether the timeline is out. */
  timing: boolean;
  loop: boolean;
  /** Whether to say, this once, that anything on the page can be picked. */
  hint: boolean;
  drawingPath: boolean;
  /** The artboard's layers, shared by the scope picker and timing view. */
  strip: { length: number; children: readonly StripInput[] } | null;
  selected: readonly string[];
  playhead: () => { seconds: number } | null;
  onPlay: () => void;
  onPause: () => void;
  /** Puts a recipe on the page, adjusted or as written; `play` watches it afterwards. */
  onRecipe: (styleId: string, settings: RecipeSettings, play: boolean) => void;
  /** Plays the page from the start: what a recipe's gear does when a slider is let go. */
  onPlayPage: () => void;
  /** Takes every animation off the page. */
  onRemove: () => void;
  /** One change, one undo step; `watch` then plays the page from just before that part of the layer. */
  onEdit: (change: LayerEdit, watch?: Phase) => void;
  /** A move to try on the picked layer without writing it, or null to stop trying. */
  onTry: (tried: { animation: LayerAnimation; hold: number } | null) => void;
  onWholePage: () => void;
  onClose: () => void;
  onTiming: () => void;
  onLoop: () => void;
  onDrawPath: () => void;
  onSelect: (id: string) => void;
  onWindow: (id: string, window: Span) => void;
  onDelay: (id: string, delay: number) => void;
  onResizeMotion: (id: string, phase: MotionPhase, resize: MotionResize) => void;
  onLength: (seconds: number) => void;
  onSeek: (seconds: number) => void;
  /** Measured dock height, shared by canvas insets and both side panels. */
  onHeight: (height: number) => void;
  /** The recipe that suits this page (Jev, from a summary of it), marked Suggested while the page wears none. */
  suggestRecipe?: (pageId: string) => Promise<string | null>;
}) {
  // The recipe this page suits, asked once per page (it only marks a card; nothing is put on the page).
  const [suggested, setSuggested] = useState<{ page: string; id: string } | null>(null);
  const pageId = target?.page.id ?? null;
  useEffect(() => {
    if (!pageId || !suggestRecipe) return;
    let live = true;
    void suggestRecipe(pageId).then((id) => { if (live) setSuggested(id ? { page: pageId, id } : null); }).catch(() => {});
    return () => { live = false; };
  }, [pageId, suggestRecipe]);
  /** Which of the three questions is showing. Kept from layer to layer: somebody setting every layer's Goes Out stays on it. */
  const [phase, setPhase] = useState<Phase>('in');
  const phaseId = useId();
  const more = useMenu();
  const way = useMenu();
  const gear = useMenu();

  const root = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    const say = () => onHeight(el.offsetHeight);
    const observer = new ResizeObserver(say);
    observer.observe(el);
    say();
    return () => observer.disconnect();
  }, [onHeight]);

  // The clock beside Play, while the timeline is out: written straight to the element.
  const length = target?.page.length ?? 0;
  const clock = useRef<HTMLSpanElement>(null);
  const read = useRef(playhead);
  read.current = playhead;
  useEffect(() => {
    if (!timing) return;
    let frame = 0;
    let said = '';
    const tick = () => {
      const at = read.current();
      const next = `${tenths(Math.min(length, at?.seconds ?? 0))} / ${tenths(length)} s`;
      if (next !== said && clock.current) clock.current.textContent = said = next;
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [timing, length]);

  const layer = target?.layer ?? null;
  // Whatever is chosen is brought into view: the recipe the page wears, the move a layer has.
  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scroller.current?.querySelector('[aria-pressed="true"], [aria-checked="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [recipe?.id, layer?.id, phase]);
  // …and so is the recipe suggested for a page that wears none, wherever it sits along the strip.
  const suggestedId = !recipe && !layer && suggested?.page === pageId ? suggested.id : null;
  useEffect(() => {
    if (suggestedId) scroller.current?.querySelector('[data-suggested]')?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [suggestedId]);

  if (!target) {
    return (
      <section ref={root} aria-label="Animate" className="animate-bar animate-studio animate-studio-empty pointer-events-auto">
        Select an artboard to animate.
        <button type="button" className="animate-studio-icon" aria-label="Close Animate" onClick={onClose}><Icon name="close" size={ICON_SIZE.small} /></button>
      </section>
    );
  }

  const make = (motion: MotionType): LayerAnimation => ({ ...defaultLayerAnimation(motion), ease: 'smooth' });
  /** What the layer plays: its own, else what its page gives it. */
  const base = layer ? (layer.animation ?? layer.inherited) : null;
  const current: PresetValue = layer ? (presetOf(base, phase) ?? 'none') : 'none';
  const values = base ? phaseValues(base, phase) : null;
  const shown = controlsFor(phase, current, { layerType: layer?.type });
  const by = textByOf(base);
  const set = (one: Phase) => (presetOf(base, one) ?? 'none') !== 'none';

  // A layer's own animation replaces the page's entirely, so every change starts from
  // what it was already playing and changes one thing.
  const change = (how: (from: LayerAnimation) => LayerAnimation, watch: Phase | undefined) =>
    onEdit((own, inherited) => {
      const from = own ?? inherited;
      return from ? how(from) : own;
    }, watch);
  const choose = (value: PresetValue) => {
    onTry(null);
    // A layer's move picked by hand: after a few the hints point at Timing (lib/hints).
    emitHintSignal('animate.layerEdited');
    onEdit((own, inherited) => withPreset(own ?? inherited, phase, value, make), value === 'none' ? undefined : phase);
  };

  const running = playback === 'playing';

  const groups = recipeGroups(MAGIC_STYLES as readonly (RecipeLike & { id: string; name: string; group?: string })[]);

  const scopeName = layer ? (layer.ids.length > 1 ? `${layer.ids.length} selected layers` : layer.name) : 'Whole artboard';
  const scopeOptions: [string, string][] = [[target.page.id, 'Whole artboard']];
  if (layer) scopeOptions.push([layer.id, scopeName]);
  for (const child of strip?.children ?? []) {
    if (child.id !== layer?.id) scopeOptions.push([child.id, child.name]);
  }

  // The numbers (seconds, delay, easing) come out with Timing or with the Pro tools.
  const pro = useProTools();
  const numbers = timing || pro;
  return (
    <section ref={root} aria-label="Animate" className="animate-bar animate-studio pointer-events-auto" data-scope={layer ? 'layer' : 'artboard'}>
      <header className="animate-studio-header">
        <h2><Icon name="motion" size={19} />Animate</h2>
        <div className="animate-studio-scope">
          <span className="animate-artboard-name" {...tip(target.page.name)}>{target.page.name}<span aria-hidden>/</span></span>
          <PopUpButton label="Animation scope" value={layer?.id ?? target.page.id} options={scopeOptions} onChange={(id) => {
            onTry(null);
            if (id === target.page.id) onWholePage();
            else onSelect(id);
          }} />
        </div>
        {layer ? (
          <div className="animate-phase-tabs" role="tablist" aria-label="Animation phase">
            {STUDIO_PHASES.map(([one, name]) => (
              <button key={one} type="button" role="tab" id={`${phaseId}-${one}`} aria-controls={`${phaseId}-content`} aria-selected={phase === one} tabIndex={phase === one ? 0 : -1}
                onKeyDown={(event) => {
                  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                  event.preventDefault();
                  event.stopPropagation();
                  const index = STUDIO_PHASES.findIndex(([value]) => value === one);
                  const next = event.key === 'Home' ? 0 : event.key === 'End' ? 2 : (index + (event.key === 'ArrowRight' ? 1 : 2)) % 3;
                  const nextPhase = STUDIO_PHASES[next];
                  if (!nextPhase) return;
                  onTry(null);
                  setPhase(nextPhase[0]);
                  event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
                }}
                onClick={() => { onTry(null); setPhase(one); }}>
                {name}{set(one) ? <span aria-label="Animation set" className="animate-phase-dot" /> : null}
              </button>
            ))}
          </div>
        ) : <span className="animate-studio-spacer" />}
        <div className="animate-studio-actions">
          <button type="button" className="animate-studio-button" aria-pressed={timing} onClick={() => { onTry(null); if (!timing) emitHintSignal('animate.timingOpened'); onTiming(); }}>
            <Icon name="timer" size={16} />Timing
          </button>
          <button type="button" className="animate-preview" disabled={!target.page.moves} aria-label={running ? 'Pause animation' : 'Preview animation'}
            onClick={() => { onTry(null); emitHintSignal('animate.played', { viaButton: true }); (running ? onPause : onPlay)(); }} {...tip(running ? 'Pause animation' : 'Preview animation', 'Space')}>
            {running ? <span className="animate-pause-icon" aria-hidden /> : <Icon name="play" size={15} />}{running ? 'Pause' : 'Preview'}
          </button>
          <button ref={more.ref} type="button" className="animate-studio-icon" aria-label="Animation options" aria-haspopup="menu" aria-expanded={more.open} onClick={more.toggle} {...tip('Animation options')}><Icon name="more" size={18} /></button>
          <button type="button" className="animate-studio-icon" aria-label="Close Animate" onClick={onClose} {...tip('Close Animate')}><Icon name="close" size={ICON_SIZE.small} /></button>
        </div>
      </header>

      <div className={`animate-studio-body animate-studio-gallery ${timing ? 'animate-studio-gallery-compact' : ''}`} id={`${phaseId}-content`} role={layer ? 'tabpanel' : undefined} aria-labelledby={layer ? `${phaseId}-${phase}` : undefined}>
        {layer ? (
          <Strip ref={scroller} className="animate-preset-strip">
            <div role="radiogroup" aria-label={`${STUDIO_PHASES.find(([one]) => one === phase)?.[1]} presets`} className="flex gap-[10px]">
              {presetsFor(phase, { scope: 'layer', layerType: layer.type, current }).map((preset) => (
                <MoveTile key={preset.value} phase={phase} motion={preset.value} name={preset.name} direction={values?.direction ?? 'up'} ease={values?.ease ?? 'smooth'} picture={picture} on={current === preset.value}
                  onPick={() => choose(preset.value)} onTry={(trying) => onTry(trying ? tryOut(base, phase, preset.value, make) : null)} />
              ))}
            </div>
          </Strip>
        ) : (
          <Strip ref={scroller} className="animate-preset-strip">
            <Group title="Artboard"><MoveTile phase="in" motion="none" name="None" direction="up" ease="smooth" picture={picture} on={!target.page.moves} onPick={onRemove} onTry={() => {}} /></Group>
            {groups.map((group) => (
              <Group key={group.title} title={group.title}>
                {group.items.map((style) => (
                  <div key={style.id} className={`relative ${CARD}`} data-suggested={suggestedId === style.id || undefined}>
                    <RecipeTile id={style.id} name={style.name} on={style.id === recipe?.id} onPick={() => { emitHintSignal('animate.recipe'); onRecipe(style.id, {}, true); }} />
                    {suggestedId === style.id ? <span className="pointer-events-none absolute left-[6px] top-[6px] rounded-[6px] bg-[var(--accent)] px-[6px] py-[2px] text-[10px] font-semibold leading-[13px] text-white">Suggested</span> : null}
                  </div>
                ))}
              </Group>
            ))}
          </Strip>
        )}
      </div>

      {timing && strip ? <div className="animate-studio-timing">
        <div className="animate-timing-help"><span>Timeline</span><span>Drag edges to resize · Drag an Enter animation to move it</span></div>
        <TimingStrip data={strip} selected={selected} playhead={playhead} onSelect={onSelect}
          onPhase={(id, next) => { onTry(null); onSelect(id); setPhase(next); }}
          onResizeMotion={onResizeMotion} onTiming={onWindow} onDelay={onDelay} onLength={onLength} onSeek={onSeek} />
      </div> : null}

      <footer className="animate-studio-footer">
        {layer ? (
          current !== 'none' && values ? (
            <div className="animate-studio-settings">
              {/* Seconds, delays and easing names are a motion program's words (Tarek, 30 Sep: "it's for
                  normal people"). They show with Timing open, or with the Pro tools on; otherwise one
                  Slow–Fast slider sets the pace. */}
              {shown.speed && !numbers ? <div className="animate-studio-field animate-pace"><span>Slow</span><Slider label="Pace" value={paceOf(values.span)} min={0} max={100} step={1} onChange={(pace) => change((from) => withPhase(from, phase, { span: spanOf(pace) }), undefined)} /><span>Fast</span></div> : null}
              {shown.speed && numbers ? <div className="animate-studio-field"><span>{phase === 'loop' ? 'Cycle' : 'Duration'}</span><NumberField label={phase === 'loop' ? 'Cycle duration' : 'Motion duration'} value={values.span} min={0.05} max={10} step={0.05} decimals={2} suffix="s" onChange={(span) => change((from) => withPhase(from, phase, { span }), undefined)} /></div> : null}
              {phase === 'in' && numbers ? <div className="animate-studio-field"><span>Delay</span><NumberField label="Animation delay" value={base?.delay ?? 0} min={0} max={60} step={0.05} decimals={2} suffix="s" onChange={(delay) => change((from) => ({ ...normalise(from), delay }), undefined)} /></div> : null}
              {phase !== 'loop' && numbers ? <div className="animate-studio-field animate-easing-field"><span>Easing</span><PopUpButton label="Animation easing" value={values.ease} options={EASING_OPTIONS} onChange={(ease) => change((from) => withPhase(from, phase, { ease }), phase)} /></div> : null}
              {shown.direction ? <button ref={way.ref} type="button" className="animate-studio-button" aria-haspopup="dialog" aria-expanded={way.open} aria-label={`Direction: ${DIRECTION_NAMES[values.direction] ?? values.direction}`} onClick={way.toggle}><Arrow angle={arrowAngle(values.direction)} /><span>Direction</span></button> : null}
              {shown.path ? <button type="button" className="animate-studio-button" aria-pressed={drawingPath} onClick={onDrawPath}><Icon name="pen" size={16} />{drawingPath ? 'Drag on the artboard' : base?.path?.length ? 'Redraw path' : 'Draw path'}</button> : null}
              {shown.textBy ? <PopUpButton label="Animate text" value={by} options={TEXT_BY_NAMES} onChange={(textBy) => change((from) => ({ ...normalise(from), textBy }), 'in')} /> : null}
            </div>
          ) : <span className="animate-studio-note">Choose a preset for {scopeName}.</span>
        ) : (
          <div className="animate-studio-settings">
            {numbers ? <div className="animate-studio-field"><span>Artboard duration</span><NumberField label="Artboard duration" value={target.page.duration} min={0.1} max={60} step={0.1} suffix="s" onChange={onLength} /></div> : null}
            {recipe && choices ? <button ref={gear.ref} type="button" className="animate-studio-button" aria-haspopup="dialog" aria-expanded={gear.open} onClick={gear.toggle}><Icon name="settings" size={16} />Adjust recipe</button> : null}
            {hint ? <span className="animate-studio-note animate-scope-hint">Select a layer to animate it on its own.</span> : null}
          </div>
        )}
        <span className="animate-studio-status">
          {timing ? <span ref={clock} aria-label="Playback time" className="tnum" /> : <><Icon name={layer && current !== 'none' ? 'check' : 'nav-artboard'} size={14} /><span>{layer ? `${presetName(phase, current)} · ${scopeName}` : recipe ? `${MAGIC_STYLES.find(style => style.id === recipe.id)?.name ?? recipe.id} · Whole artboard` : 'Whole artboard'}</span></>}
        </span>
      </footer>

      <Menu open={more.open} anchor={more.anchor} onClose={more.close} placement="above" align="end" label="Animation options" width={220} items={[
        { id: 'loop', label: 'Loop preview', checked: loop, onSelect: onLoop },
        { kind: 'separator' },
        { id: 'remove', label: 'Remove all animations', danger: true, disabled: !target.page.moves, onSelect: onRemove },
      ]} />

      {recipe && choices ? (
        <Popover open={gear.open && !layer} anchor={gear.anchor} onClose={gear.close} placement="above" align="center" width={268} label="Adjust the recipe">
          <RecipeGear recipe={recipe} choices={choices} onChange={(settings, play) => onRecipe(recipe.id, settings, play)} onLetGo={onPlayPage} />
        </Popover>
      ) : null}

      <Popover open={way.open && shown.direction} anchor={way.anchor} onClose={way.close} placement="above" align="center" label="Direction">
        <div role="radiogroup" aria-label="Direction" className="grid grid-cols-3 gap-[3px] p-[8px]">
          {PAD.map((one, at) =>
            one ? (
              <button
                key={one}
                type="button"
                role="radio"
                aria-checked={values?.direction === one}
                aria-label={DIRECTION_NAMES[one]}
                {...tip(DIRECTION_NAMES[one]!)}
                onClick={() => {
                  way.close();
                  change((from) => withPhase(from, phase, { direction: one as Direction }), phase);
                }}
                className={`grid size-[28px] place-items-center rounded-[8px] transition-colors ${values?.direction === one ? 'bg-accent text-on-accent' : 'bg-fill-2 text-label-2 hover:bg-fill-selected hover:text-label'}`}
              >
                <Arrow angle={arrowAngle(one)} />
              </button>
            ) : (
              <span key={`mid-${at}`} aria-hidden className="grid size-[28px] place-items-center">
                <span className="size-[4px] rounded-full bg-label-3" />
              </span>
            ),
          )}
        </div>
      </Popover>
    </section>
  );
}

const tenths = (seconds: number) => (Math.round(seconds * 10) / 10).toFixed(1);

/** A card's slot in the strip: every recipe and every move is this wide. */
const CARD = 'w-[132px] shrink-0';

/**
 * What a recipe's gear opens: the few things about the WHOLE recipe worth turning — and
 * only the ones that do something that can be seen ON THIS PAGE (the engine's
 * `magicChoices`, which tries each against the page). Text by the word is not offered on a
 * page with no words, nor a direction where nothing travels far enough to tell: a control
 * that changes nothing reads as a control that is broken. Every change writes the recipe
 * again, adjusted, as one undo step; a slider plays the page once it is let go.
 */
function RecipeGear({ recipe, choices, onChange, onLetGo }: { recipe: Recipe; choices: MagicChoices; onChange: (settings: RecipeSettings, play: boolean) => void; onLetGo: () => void }) {
  const options = magicOptions(recipe.id);
  const style = MAGIC_STYLES.find((one) => one.id === recipe.id);
  if (!options || !style) return null;
  const { id: _id, ...settings } = recipe;
  const adjusted = Object.keys(settings).length > 0;
  const by = choices.textBy;
  const direction = choices.direction ? (settings.direction ?? options.direction) : null;
  const row = 'flex items-center gap-[8px] text-[12px] text-label-2';
  const letGo = { onPointerUp: onLetGo, onKeyUp: (event: { key: string }) => (event.key.startsWith('Arrow') ? onLetGo() : undefined) };
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[12px] p-[12px]">
      <div className="flex items-center gap-[8px]">
        <strong className="min-w-0 flex-1 truncate text-[13px] font-semibold text-label">{style.name}</strong>
        {adjusted ? (
          <button type="button" onClick={() => onChange({}, true)} className="h-[22px] shrink-0 rounded-full px-[8px] text-[12px] text-accent-text transition-colors hover:bg-fill-2">
            Reset
          </button>
        ) : null}
      </div>
      <div className={row} {...letGo}>
        <span className="w-[52px] shrink-0">Slow</span>
        <Slider label="Speed" className="w-0 min-w-0 flex-1" value={paceValue(settings.speed)} min={0} max={100} step={1} onChange={(value) => onChange({ ...settings, speed: paceAt(value) }, false)} />
        <span className="w-[38px] shrink-0 text-right">Fast</span>
      </div>
      {choices.spread ? (
        <div className={row} {...letGo}>
          <span className="w-[52px] shrink-0">Together</span>
          <Slider
            label="How far apart things set off"
            className="w-0 min-w-0 flex-1"
            value={spreadValue(settings.spread, options.spreadMost)}
            min={0}
            max={100}
            step={1}
            onChange={(value) => onChange({ ...settings, spread: spreadAt(value, options.spreadMost) }, false)}
          />
          <span className="w-[38px] shrink-0 text-right">Apart</span>
        </div>
      ) : null}
      {by ? (
        <SegmentedControl
          size="small"
          label="Text arrives"
          value={by.now}
          onChange={(textBy) => onChange({ ...settings, textBy }, true)}
          segments={TEXT_BY_NAMES.filter(([value]) => by.ways.includes(value)).map(([value, name]) => ({ value, label: name }))}
        />
      ) : null}
      {direction ? (
        <div className="flex items-center gap-[12px]">
          <span className="min-w-0 flex-1 text-[12px] text-label-2">Moves {DIRECTION_NAMES[direction] ?? direction}</span>
          <div role="radiogroup" aria-label="Direction" className="grid shrink-0 grid-cols-3 gap-[3px]">
            {PAD.map((one, at) =>
              one ? (
                <button
                  key={one}
                  type="button"
                  role="radio"
                  aria-checked={direction === one}
                  aria-label={DIRECTION_NAMES[one]}
                  onClick={() => onChange({ ...settings, direction: one as Direction }, true)}
                  className={`grid size-[24px] place-items-center rounded-[7px] transition-colors ${direction === one ? 'bg-accent text-on-accent' : 'bg-fill-2 text-label-2 hover:bg-fill-selected hover:text-label'}`}
                >
                  <Arrow angle={arrowAngle(one)} />
                </button>
              ) : (
                <span key={`mid-${at}`} aria-hidden className="grid size-[24px] place-items-center">
                  <span className="size-[4px] rounded-full bg-label-3" />
                </span>
              ),
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The strip: one row to pick from, scrolling sideways, its ends fading out. A wheel that
 * only turns one way still moves it — most mice have no sideways wheel.
 */
function Strip({ ref, className, children }: { ref: React.Ref<HTMLDivElement>; className: string; children: ReactNode }) {
  const wheel = (event: WheelEvent<HTMLDivElement>) => {
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) event.currentTarget.scrollLeft += event.deltaY;
  };
  return (
    <div ref={ref} onWheel={wheel} className={`animate-strip flex gap-[16px] overflow-x-auto pb-[2px] pl-[12px] pr-[24px] pt-[3px] ${className}`}>
      {children}
    </div>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex shrink-0 flex-col gap-[5px]">
      <span className="h-[13px] truncate pl-[3px] text-[11px] font-semibold uppercase leading-[13px] tracking-[0.04em] text-label-3">{title}</span>
      <div className="flex gap-[8px]">{children}</div>
    </div>
  );
}
